import { Procedure } from '../define-procedure';
import { Store, Actions, ofActionDispatched } from '@ngxs/store';
import { MessageEnum } from '../models/message.enum';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { RPCEvent } from '../define-event';
import { TakeChapterPicture, TakeChapterPictureSuccess, SetSelectedChapterBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { flatMap, take, catchError, tap } from 'rxjs/operators';
import { ImageService } from 'src/app/shared/image.service';
import { of } from 'rxjs/internal/observable/of';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { EngineService } from 'src/app/engine/engine.service';
import { HumanInterface } from '../models/human.interface';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';
import { ViewActions } from 'src/app/view/state/view.actions';
import { ViewState } from 'src/app/view/state/view.state';
import { WidgetWiring } from 'src/app/shared/widget-wiring';
import { UiIO } from '../models/ui.io';
import { isEmpty } from 'lodash';
import { ScrollModeEnum } from 'src/app/engine/models/scroll-mode.enum';
import { SetScrollMode } from 'src/app/engine/state/engine.actions';

function toAPIColor(input: BackgroundModel): UiIO.UIBackgroundParam {
  const { colors, gradientType, colorStops } = input;
  return {
    // normalize: [] -> [[]]
    colors: [
      [colors[0], colors[1], colors[2], colors[3]],
      [colors[4], colors[5], colors[6], colors[7]]
    ],
    // as-is
    gradientType,
    colorStops
  };
}

function normalizeColorInput(input: any): [number, number, number, number][] {
  const paramColorValues = ColorUtils.getColors(input)
    .map((parsedColor) => parsedColor['255']);

  return paramColorValues;
}

// #TODO[AC]: should be deprecated in the future and handled by the client's code
function openImageInTab(imageData: string) {
  // iframe needed to bypass data url restriction in window.open
  const imageWindow = window.open('', '_blank');
  if (imageWindow) {
    const imageFrame = imageWindow.document.createElement('iframe');
    imageFrame.src = imageData;
    imageWindow.document.body.style.margin = '0';
    imageFrame.style.border = 'none';
    imageFrame.style.height = '100%';
    imageFrame.style.width = '100%';
    imageWindow.document.body.appendChild(imageFrame);
  }
}

export function getUIEvents(): RPCEvent<UiIO.UIEventResponse>[] {
  return [

  ];
}

export function getUIProcedures(
  Human: HumanInterface,
  store: Store,
  actions: Actions,
  engineService: EngineService,
  imageService: ImageService
): Procedure<UiIO.UIProcRequest, UiIO.UIProcResponse>[] {
  return [
    // gets the current chapter's background color
    {
      procName: MessageEnum.UIGetBackground,
      actionFn: () => {
        const background = store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
        return toAPIColor(background);
      }
    },
    // sets the current chapter's background color
    {
      procName: MessageEnum.UISetBackground,
      // normalize color values
      mapFnIn: (params: UiIO.UIRawBackgroundParam) => {
        const { colors, colorStops, gradientType } = params;
        return <UiIO.UIBackgroundParam>{
          // normalize color input
          colors: colors ? normalizeColorInput(colors) : colors,
          gradientType,
          colorStops
        };
      },
      actionFn: (params: UiIO.UIBackgroundParam) => {
        if (params) {
          // get current background
          const currentBackground = store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
          const { colors, colorStops, gradientType } = currentBackground;

          // get optional user configured values, else current
          const targetColors = params.colors ? [].concat(...params.colors) : colors;
          const targetGradientType = params.gradientType || gradientType;
          const targetColorStops = params.colorStops || colorStops;

          // set app background
          const background = new BackgroundModel(targetColors, targetGradientType, targetColorStops);
          store.dispatch(new SetSelectedChapterBackground(background));
        }
      }
    },
    // gets the x, y, z world postion of the target point
    {
      procName: MessageEnum.UIGetWordPosition,
      actionFn: (params: UiIO.UIWorldPositionRequest) => {
        const { worldPos } = Human.renderer.pick.pick(params.x, params.y, { rayPick: true });
        if (worldPos) {
          const { 0: x, 1: y, 2: z } = worldPos;
          return <UiIO.UIWordPositionResponse>{ x, y, z };
        } else {
          return null;
        }
      }
    },
    {
      procName: MessageEnum.UISetLoadingExperience,
      actionFn: (loaderType: UiIO.UISetLoadingExperienceRequest) => {
        if (!isEmpty(loaderType)) {
          store.dispatch([
            new ViewActions.SetLoaderType(loaderType),
            new ViewActions.ToggleLoader(true)
          ]);
        } else {
          store.dispatch(new ViewActions.ToggleLoader(false));
        }
        return true;
      }
    },
    {
      procName: MessageEnum.UISetScrollCaptureMode,
      actionFn: (scrollCaptureMode: UiIO.UISetScrollCaptureModeRequest) => {
        store.dispatch(new SetScrollMode(scrollCaptureMode));
        return true;
      }
    },
    // gets the display config
    {
      procName: MessageEnum.UIGetDisplay,
      actionFn: () => {
        const layout = store.selectSnapshot(ViewState.getLayout);
        const legacyDisplayInfo: UiIO.UIDisplayParamsExternal = {
          audio: layout.audio.enable,
          fullscreen: layout.fullscreen.enable,
          help: layout.help.enable,

          // these all map from Camera
          nav: layout.camera.enable,
          cameraMode: layout.camera.enable,
          center: layout.camera.enable,
          zoomIn: layout.camera.enable,
          zoomOut: layout.camera.enable,

          info: layout.column.enable || layout.drawer.enable,

          // these all map from Media
          playPause: layout.media.enable,
          scrubber: layout.media.enable,
          'scrubber.chapters': layout.media.enable,
          
          tools: layout.tools.enable,
          'tools-display': layout.tools.enable,
          tour: layout.pagination.enable,
          'tour.chapters': layout.pagination.enable,
          objectTree: layout.search.enable,

          // legacy
          share: false,
          dissect: false,
          xray: false,
          annotations: false,
        };
        return legacyDisplayInfo;
      }
    },
    // sets the display config
    {
      procName: MessageEnum.UISetDisplay,
      mapFnIn: (params: UiIO.UIDisplayParams) => {
        // get the current layout
        const layout = store.selectSnapshot(ViewState.getLayout);

        // upgrade params to reflect new widget semantics
        const commonConfig = WidgetWiring.convertAPIUIConfigToUIConfig(params);

        // create a new layout
        return WidgetWiring.createLayoutFromConfig(layout, commonConfig);
      },
      actionFn: (layout: UiIO.UISetLayoutRequest) => {
        // have our new layout take effect

        // noop for now
        store.dispatch(new ViewActions.SetLayout(layout));
      }
    },
    // resets the UI to the initial layout
    {
      procName: MessageEnum.UIResetDisplay,
      actionFn: () => {
        const initialLayout = store.selectSnapshot(ViewState.getInitialLayout);
        store.dispatch(new ViewActions.SetLayout(initialLayout));
      }
    },
    // take a snapshot of the current chapter
    {
      procName: MessageEnum.UISnapshot,
      aliases: [MessageEnum.SceneSnapshot],
      // normalize params: extend defaults
      mapFnIn: (params: UiIO.UISnapshotParams) => {
        const { width, height } = engineService.getCanvas();
        return {
          width: width,
          height: height,
          openInTab: false,
          ...params
        };
      },
      actionFn: (params: UiIO.UISnapshotParams) => {
        const { width, height, openInTab } = params;
        const currentChapterId = store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
        const dimensions = { width, height };
        return store.dispatch(new TakeChapterPicture(currentChapterId, dimensions, true, true))
          .pipe(
            // extract success
            flatMap(() => {
              return actions.pipe(
                ofActionDispatched(TakeChapterPictureSuccess),
                take(1)
              );
            }),
            // serialize
            flatMap((e: TakeChapterPictureSuccess) => imageService.blobToBase64(e.blob)),
            // open image tab: optional
            tap((imageData: string) => {
              if (openInTab && imageData) {
                openImageInTab(imageData);
              }
            }),
            // handle errors: empty response
            catchError(() => of(null))
          );
      }
    }
  ];
}
